// src/pages/MovieDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getMovieById, getAllMovies } from "../utils/movieUtils";
import VideoPlayer from "../components/VideoPlayer";
import { getCurrentUser, toggleFavorite, isFavorite } from "../utils/auth";
import { handleImgError } from "../utils/thumbnailUtils";
import { PageSEO, buildMovieSchema } from "../hooks/useSEO";
import "../styles/styles.css";

const MovieDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [movie, setMovie] = useState(null);
  const [allMovies, setAllMovies] = useState([]);
  const [showFullSynopsis, setShowFullSynopsis] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [user] = useState(() => getCurrentUser());

  useEffect(() => {
    const found = getMovieById(id);
    if (!found) {
      navigate("/movies");
    } else {
      setMovie(found);
      setAllMovies(getAllMovies());
      if (user && user.role === "user") {
        setFavorite(isFavorite(user.email, found.id));
      }
      window.scrollTo(0, 0);
    }
  }, [id, navigate, user]);

  const handleFavorite = () => {
    if (!user) { navigate("/login"); return; }
    if (user.role !== "user") return;
    const updated = toggleFavorite(user.email, movie.id);
    setFavorite(updated.includes(movie.id));
  };

  if (!movie) return null;

  const goBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/movies");
  };

  // Películas relacionadas: mismo género, distinto id
  const related = allMovies
    .filter(m => m.id !== movie.id && m.genre.some(g => movie.genre.includes(g)))
    .slice(0, 4);

  const synopsis = showFullSynopsis
    ? (movie.longSynopsis || movie.synopsis)
    : movie.synopsis;

  const ageColors = {
    "TP": "#4ade80",
    "12+": "#fbbf24",
    "16+": "#f87171",
    "18+": "#dc2626",
  };

  return (
    <div className="detail-page">
      <PageSEO
        title={movie.title}
        description={movie.synopsis}
        image={movie.thumbnail}
        url={`/movies/${movie.id}`}
        type="video.movie"
        schema={buildMovieSchema({
          title: movie.title,
          synopsis: movie.synopsis,
          thumbnail: movie.thumbnail,
          releaseDate: movie.releaseDate,
          genres: movie.genre,
          duration: movie.duration,
          url: `/movies/${movie.id}`,
        })}
      />

      {/* Hero */}
      <div className="detail-hero" style={{ backgroundImage: `url(${movie.thumbnail})` }}>
        <div className="detail-hero-overlay" />
        <button className="detail-back-btn" onClick={goBack}>← Volver</button>
        <div className="detail-hero-content">
          <div className="detail-badges-row">
            <span className="detail-type-badge">PELÍCULA IA</span>
            <span className="detail-ai-badge" title="Contenido generado íntegramente con inteligencia artificial">🤖 AI-Generated</span>
            {movie.ageRating && (
              <span className="detail-age-badge" style={{ borderColor: ageColors[movie.ageRating], color: ageColors[movie.ageRating] }}>
                {movie.ageRating}
              </span>
            )}
            {movie.quality && <span className="detail-quality-badge">{movie.quality}</span>}
          </div>
          <h1 className="detail-title-hero">{movie.title}</h1>
          <div className="detail-meta-row">
            <span>{movie.duration}</span>
            <span>·</span>
            <span>{new Date(movie.releaseDate).getFullYear()}</span>
            <span>·</span>
            <span>{movie.genre.join(", ")}</span>
          </div>
          <div className="detail-hero-actions">
            <button
              className="btn-primary btn-play-big"
              onClick={() => document.getElementById("player-section").scrollIntoView({ behavior: "smooth" })}
            >
              ▶ Ver película
            </button>
            <button
              className={`btn-outline-icon ${favorite ? "favorited" : ""}`}
              onClick={handleFavorite}
              title={favorite ? "Quitar de Mi Lista" : "Añadir a Mi Lista"}
            >
              {favorite ? "✓ En Mi Lista" : "+ Mi Lista"}
            </button>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="detail-body">
        <div className="detail-main-grid">
          <div className="detail-left">
            <div className="detail-synopsis-block">
              <p className="detail-synopsis-text">{synopsis}</p>
              {movie.longSynopsis && (
                <button
                  className="detail-synopsis-toggle"
                  onClick={() => setShowFullSynopsis(!showFullSynopsis)}
                >
                  {showFullSynopsis ? "Ver menos ▲" : "Leer más ▼"}
                </button>
              )}
            </div>

            {movie.tags && (
              <div className="detail-tags">
                {movie.tags.map(tag => (
                  <span key={tag} className="detail-tag">#{tag}</span>
                ))}
              </div>
            )}

            <div className="detail-tech-sheet">
              <h3 className="detail-section-title">Ficha técnica</h3>
              <div className="detail-tech-grid">
                {movie.language && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Idioma</span>
                    <span className="detail-tech-value">{movie.language}</span>
                  </div>
                )}
                {movie.subtitles && movie.subtitles.length > 0 && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Subtítulos</span>
                    <span className="detail-tech-value">{movie.subtitles.join(", ")}</span>
                  </div>
                )}
                <div className="detail-tech-item">
                  <span className="detail-tech-label">Duración</span>
                  <span className="detail-tech-value">{movie.duration}</span>
                </div>
                <div className="detail-tech-item">
                  <span className="detail-tech-label">Estreno</span>
                  <span className="detail-tech-value">{movie.releaseDate}</span>
                </div>
                {movie.director && (
                  <div className="detail-tech-item">
                    <span className="detail-tech-label">Dirección IA</span>
                    <span className="detail-tech-value">{movie.director}</span>
                  </div>
                )}
              </div>

              {movie.cast && movie.cast.length > 0 && (
                <div className="detail-cast">
                  <span className="detail-tech-label">Voces IA</span>
                  <div className="detail-cast-list">
                    {movie.cast.map(c => (
                      <span key={c} className="detail-cast-chip">{c}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="detail-right">
            <img
              src={movie.thumbnail}
              alt={movie.title}
              className="detail-thumbnail-side"
              onError={(e) => handleImgError(e, movie.title, 800, 450)}
            />
          </div>
        </div>

        <div id="player-section" className="player-wrapper">
          <h3 className="detail-section-title">▶ Reproducir</h3>
          <VideoPlayer videoUrl={movie.videoUrl} user={user} />
        </div>

        {related.length > 0 && (
          <div className="detail-related">
            <h3 className="detail-section-title">También te puede gustar</h3>
            <div className="detail-related-grid">
              {related.map(m => (
                <div
                  key={m.id}
                  className="detail-related-card"
                  onClick={() => navigate(`/movies/${m.id}`)}
             
                >
                  <div className="detail-related-img">
                    <img
                      src={m.thumbnail}
                      alt={m.title}
                      onError={(e) => handleImgError(e, m.title, 400, 225)}
                    />
                    <div className="detail-related-overlay">▶</div>
                  </div>
                  <div className="detail-related-info">
                    <p className="detail-related-title">{m.title}</p>
                    <p className="detail-related-meta">{m.duration} · {m.genre[0]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MovieDetail;
